import { useMemo } from 'react';
import type { ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type ICrudService from '@dr-pam/common-components/Services/ICrudService';
import hash from 'object-hash';
import type {
	SectionImageListFragment,
	SectionImageSingleFragment,
	SectionImageCreateInput,
	SectionImageUpdateInput,
	SectionImageQuery,
	SectionImageQueryVariables,
	CreateSectionImageMutation,
	CreateSectionImageMutationVariables,
	UpdateSectionImageMutation,
	UpdateSectionImageMutationVariables,
	DeleteSectionImagesMutation,
	DeleteSectionImagesMutationVariables,
	SectionImageCategoryListFragment,
	AddSectionImageToCategoryMutation,
	AddSectionImageToCategoryMutationVariables,
	RemoveSectionImageFromCategoryMutation,
	RemoveSectionImageFromCategoryMutationVariables,
} from '../graphql/graphql';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import ApolloUtils from '@dr-pam/common-components/Utils/ApolloUtils';
import { SortableTreeItem } from '../models/SortableTreeItem';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';

export type SectionImageFragment = {
	id: string;
	isPinned: boolean;
	sortOrder: number;
	isRequired: boolean;
	image: {
		id: string;
		filename: string;
		url: string;
	};
};

export class SectionImageSortableTreeItem<T extends SectionImageCategoryListFragment> extends SortableTreeItem<T> {
	public getId(): string {
		return this.item.id;
	}
	public getSortOrder(): number {
		return this.item.sortOrder;
	}
	public setSortOrder(sortOrder: number): void {
		this.item.sortOrder = sortOrder;
	}

	public getName(): string {
		if (this.parent) {
			return `${this.parent.getName()} > ${this.item.sectionImage.filename}`;
		} else {
			return this.item.sectionImage.filename ?? '';
		}
	}

	public getHash(): string {
		return hash([this.getId(), this.parent?.getId(), this.getSortOrder()]);
	}
}

export const GQL_FRAG_SECTION_IMAGE_LIST = gql`
	fragment SectionImageList on SectionImage {
		id
		filename
		url
	}
`;

export const GQL_FRAG_SECTION_IMAGE_SINGLE = gql`
	fragment SectionImageSingle on SectionImage {
		id
		created
		modified
		filename
		url
	}
`;

export const GQL_FRAG_SECTION_IMAGE_CATEGORY_SINGLE = gql`
	${GQL_FRAG_SECTION_IMAGE_LIST}
	fragment SectionImageCategorySingle on SectionImageCategory {
		id
		sortOrder
		sectionImage {
			...SectionImageSingle
		}
	}
`;

export const GQL_FRAG_SECTION_IMAGE_CATEGORY_LIST = gql`
	${GQL_FRAG_SECTION_IMAGE_LIST}
	fragment SectionImageCategoryList on SectionImageCategory {
		id
		sortOrder
		sectionImage {
			...SectionImageList
		}
	}
`;

export const GQL_GET_SECTION_IMAGE = gql`
	${GQL_FRAG_SECTION_IMAGE_SINGLE}
	query SectionImage($sectionImageId: String!) {
		sectionImage(where: { id: $sectionImageId }) {
			...SectionImageSingle
		}
	}
`;

export const GQL_GET_SECTION_IMAGES_FOR_LIST = gql`
	${GQL_FRAG_SECTION_IMAGE_LIST}
	query SectionImages {
		sectionImages(orderBy: { created: desc }) {
			...SectionImageList
		}
	}
`;

export const GQL_DELETE_SECTION_IMAGES = gql`
	mutation DeleteSectionImages($sectionImageIds: [String!]) {
		deleteManySectionImage(where: { id: { in: $sectionImageIds } }) {
			count
		}
	}
`;

export const GQL_CREATE_SECTION_IMAGE = gql`
	${GQL_FRAG_SECTION_IMAGE_SINGLE}
	mutation CreateSectionImage($sectionImage: SectionImageCreateInput!) {
		createOneSectionImage(data: $sectionImage) {
			...SectionImageSingle
		}
	}
`;

export const GQL_UPDATE_SECTION_IMAGE = gql`
	${GQL_FRAG_SECTION_IMAGE_SINGLE}
	mutation UpdateSectionImage($sectionImageId: String!, $sectionImage: SectionImageUpdateInput!) {
		updateOneSectionImage(where: { id: $sectionImageId }, data: $sectionImage) {
			...SectionImageSingle
		}
	}
`;

export const GQL_ADD_SECTION_IMAGE_TO_CATEGORY = gql`
	${GQL_FRAG_SECTION_IMAGE_CATEGORY_LIST}
	mutation AddSectionImageToCategory($sectionImageId: String!, $categoryId: String!, $sortOrder: Int) {
		upsertOneSectionImageCategory(
			where: { sectionImageCategory: { sectionImageId: $sectionImageId, categoryId: $categoryId } }
			create: {
				sectionImage: { connect: { id: $sectionImageId } }
				category: { connect: { id: $categoryId } }
				sortOrder: $sortOrder
			}
			update: {
				sectionImage: { connect: { id: $sectionImageId } }
				category: { connect: { id: $categoryId } }
				sortOrder: { set: $sortOrder }
			}
		) {
			...SectionImageCategoryList
		}
	}
`;

export const GQL_REMOVE_SECTION_IMAGE_FROM_CATEGORY = gql`
	mutation RemoveSectionImageFromCategory($sectionImageId: String!, $categoryId: String!) {
		deleteOneSectionImageCategory(
			where: { sectionImageCategory: { sectionImageId: $sectionImageId, categoryId: $categoryId } }
		) {
			id
		}
	}
`;

export default class SectionImageService implements ICrudService<
	SectionImageListFragment,
	SectionImageSingleFragment,
	SectionImageCreateInput,
	SectionImageUpdateInput
> {
	constructor(
		private readonly _apolloClient: ApolloClient,
		private readonly _registerAbort?: RegisterAbortFunction,
	) {}

	public get(sectionImageId: string) {
		const request = ApolloUtils.abortableQuery<
			SectionImageQuery,
			SectionImageSingleFragment | null,
			SectionImageQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_SECTION_IMAGE,
				variables: {
					sectionImageId,
				},
			},
			(data) => data.sectionImage ?? null,
			this._registerAbort,
		);

		return request;
	}

	public getAll() {
		const request = ApolloUtils.abortableQuery<SectionImageQuery, SectionImageListFragment[]>(
			this._apolloClient,
			{
				query: GQL_GET_SECTION_IMAGES_FOR_LIST,
			},

			(data) => [data.sectionImage!],
			this._registerAbort,
		);
		return request;
	}

	public async create(sectionImage: SectionImageCreateInput) {
		const result = await this._apolloClient.mutate<CreateSectionImageMutation, CreateSectionImageMutationVariables>(
			{
				mutation: GQL_CREATE_SECTION_IMAGE,
				variables: {
					sectionImage,
				},
			},
		);
		if (!result.data?.createOneSectionImage) {
			throw new Error('Failed to create section image');
		}
		return result.data.createOneSectionImage;
	}

	public async update(sectionImageId: string, sectionImage: SectionImageUpdateInput) {
		const result = await this._apolloClient.mutate<UpdateSectionImageMutation, UpdateSectionImageMutationVariables>(
			{
				mutation: GQL_UPDATE_SECTION_IMAGE,
				variables: {
					sectionImageId,
					sectionImage,
				},
			},
		);
		if (!result.data?.updateOneSectionImage) {
			throw new Error('Failed to update section image');
		}
		return result.data.updateOneSectionImage;
	}

	public async delete(sectionImageIds: string | string[]) {
		sectionImageIds = Array.isArray(sectionImageIds) ? sectionImageIds : [sectionImageIds];

		await this._apolloClient.mutate<DeleteSectionImagesMutation, DeleteSectionImagesMutationVariables>({
			mutation: GQL_DELETE_SECTION_IMAGES,
			variables: {
				sectionImageIds,
			},
		});
	}

	public async addSectionImageToCategory(
		sectionImageId: string,
		categoryId: string,
		sortOrder?: number,
	): Promise<SectionImageCategoryListFragment> {
		const result = await this._apolloClient.mutate<
			AddSectionImageToCategoryMutation,
			AddSectionImageToCategoryMutationVariables
		>({
			mutation: GQL_ADD_SECTION_IMAGE_TO_CATEGORY,
			variables: {
				sectionImageId,
				categoryId,
				sortOrder: sortOrder ?? 0,
			},
		});
		if (!result.data?.upsertOneSectionImageCategory) {
			throw new Error('Failed to add section image to category');
		}
		return result.data.upsertOneSectionImageCategory;
	}

	public async removeSectionImageFromCategory(sectionImageId: string, categoryId: string): Promise<void> {
		const result = await this._apolloClient.mutate<
			RemoveSectionImageFromCategoryMutation,
			RemoveSectionImageFromCategoryMutationVariables
		>({
			mutation: GQL_REMOVE_SECTION_IMAGE_FROM_CATEGORY,
			variables: {
				sectionImageId,
				categoryId,
			},
		});
		if (!result.data?.deleteOneSectionImageCategory) {
			throw new Error('Failed to remove section image from category');
		}
	}
}

export function useSectionImageService() {
	const apolloClient = useApolloClient();
	const registerAbort = useAbortRegistry();

	return useMemo(() => new SectionImageService(apolloClient, registerAbort), [apolloClient, registerAbort]);
}
